import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { AdminLoginInput } from "@steelbill/types";
import { AdminLoginForm } from "@/components/admin-login-form";
import { useAuth } from "@/features/auth/auth-context";
import { ApiError } from "@/lib/api-error";
import { routes } from "@/routes/paths";

/**
 * `/admin/login` — unlisted. See the note in `admin-login-form.tsx` for what
 * that does and does not protect.
 *
 * NOT LINKED FROM ANYWHERE, which has one consequence worth stating: if the
 * path ever changes, nothing in the app breaks visibly and nobody finds out
 * until the admin cannot sign in. `routes.adminLogin` in `paths.ts` is the only
 * definition and this file is its only consumer.
 */
export function AdminLoginScreen(): React.JSX.Element {
  const { t } = useTranslation();
  const { loginAsAdmin, staySignedIn, setStaySignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? routes.home;

  async function submit(values: AdminLoginInput): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const user = await loginAsAdmin(
        { email: values.email.trim(), password: values.password },
        staySignedIn,
      );
      // Fired before `navigate` — Sonner sits above the router, so the toast
      // survives the transition and greets on the screen he lands on.
      toast.success(t("auth.welcomeBack", { name: user.displayName }));
      navigate(from, { replace: true });
    } catch (caught) {
      /*
       * The server's own sentence when it has one. As on the owner's screen, the
       * API returns ONE message for "no such account" and "wrong password", so
       * this page cannot be used to enumerate which admin emails exist.
       */
      setError(caught instanceof ApiError ? caught.message : t("err.somethingWentWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <AdminLoginForm
          staySignedIn={staySignedIn}
          onStayChange={setStaySignedIn}
          busy={busy}
          error={error}
          onSubmit={(values) => void submit(values)}
        />
      </div>
    </div>
  );
}
